import { type CSSProperties, type ReactElement, type ReactNode, useId, useState } from 'react';
import { UiIcon, type UiIconName } from './icons';

/**
 * The parts of Configurações › Regras governadas (D762), from the Claude Design screen A12 "Regras governadas (redesign)"
 * (`Admin Web - Regras Governadas.dc.html`): the level switch, the rule list (search, segmented filter, domain groups, rows) and the cards of the
 * selected rule (value in force, proposal, value per level, history). Like the other screen families none of them accepts a length or a style:
 * every option is a token name written as a data attribute (stylesheet: `governed-rules.css`). Words come from the caller; nothing here is copy.
 */

// ---- the level switch ---------------------------------------------------------------------------------------------------------

export interface LevelOption {
  readonly id: string;
  readonly label: string;
  readonly icon: UiIconName;
}

/** Plataforma inteira › País › Estado › Cidade: a radio group, broadest first, with a chevron between two levels. */
export function LevelSwitch({ label, options, value, onChange }: { label: string; options: readonly LevelOption[]; value: string; onChange: (id: string) => void }): ReactElement {
  const labelId = useId();
  return (
    <div className="vello-gr-levels">
      <span className="vello-gr-levels__label" id={labelId}>{label}</span>
      <div className="vello-gr-levels__track" role="radiogroup" aria-labelledby={labelId}>
        {options.map((o, i) => (
          <span key={o.id} className="vello-gr-levels__item">
            {i > 0 && <span className="vello-gr-level__sep" aria-hidden="true"><UiIcon name="chevronRight" size={12} /></span>}
            <button type="button" role="radio" aria-checked={o.id === value} className="vello-gr-level" onClick={() => onChange(o.id)}>
              <UiIcon name={o.icon} size={14} />
              {o.label}
            </button>
          </span>
        ))}
      </div>
    </div>
  );
}

// ---- the workspace and the rule list --------------------------------------------------------------------------------------------

/** The rule list beside the selected rule (one column on a narrow screen). */
export function RulesWorkspace({ list, detail }: { list: ReactNode; detail: ReactNode }): ReactElement {
  return (
    <div className="vello-gr-workspace">
      {list}
      {detail}
    </div>
  );
}

/** The list's frame: a head (search + filter), the scrolling body and an optional foot. */
export function RuleListPanel({ label, head, foot, children }: { label: string; head: ReactNode; foot?: ReactNode; children?: ReactNode }): ReactElement {
  return (
    <section className="vello-gr-list" aria-label={label}>
      <div className="vello-gr-list__head">{head}</div>
      <div className="vello-gr-list__body">{children}</div>
      {foot !== undefined && <div className="vello-gr-list__foot">{foot}</div>}
    </section>
  );
}

/** The rule search, with a clear button while it holds text. */
export function RuleSearch({ label, placeholder, value, onChange, clearLabel }: { label: string; placeholder: string; value: string; onChange: (v: string) => void; clearLabel: string }): ReactElement {
  return (
    <div className="vello-gr-search">
      <UiIcon name="search" size={15} />
      <input className="vello-gr-search__input" type="search" aria-label={label} placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} />
      {value !== '' && (
        <button type="button" className="vello-gr-search__clear" aria-label={clearLabel} onClick={() => onChange('')}>
          <UiIcon name="x" size={13} />
        </button>
      )}
    </div>
  );
}

export interface SegmentOption {
  readonly id: string;
  readonly label: string;
  readonly count?: number;
}

/** A segmented filter with a sliding thumb (Todas · Em revisão · Sem valor). */
export function SegmentedFilter({ label, options, value, onChange }: { label: string; options: readonly SegmentOption[]; value: string; onChange: (id: string) => void }): ReactElement {
  const index = Math.max(0, options.findIndex((o) => o.id === value));
  const vars = { '--vello-seg-n': String(options.length), '--vello-seg-i': String(index) } as CSSProperties;
  return (
    <div className="vello-gr-seg" role="radiogroup" aria-label={label} style={vars}>
      <span className="vello-gr-seg__thumb" aria-hidden="true" />
      {options.map((o) => (
        <button key={o.id} type="button" role="radio" aria-checked={o.id === value} className="vello-gr-seg__item" onClick={() => onChange(o.id)}>
          {o.label}
          {o.count !== undefined && <span className="vello-gr-seg__count">{o.count}</span>}
        </button>
      ))}
    </div>
  );
}

/** One domain of the list: a sticky head with its count, then its rows. */
export function RuleGroup({ name, count, children }: { name: string; count: number; children?: ReactNode }): ReactElement {
  return (
    <div role="group" aria-label={name}>
      <div className="vello-gr-group" aria-hidden="true">{name}<span className="vello-gr-group__count">{count}</span></div>
      {children}
    </div>
  );
}

export type RuleSourceKind = 'own' | 'inherited' | 'none';

/** One rule of the list: the source dot, the name (italic when the vocabulary has none), the key, and the review count or where the value comes from. */
export function RuleRowButton({ name, ruleKey, showKey = true, catalogued = true, source, sourceLabel, pendingLabel, selected, density = 'comfortable', onClick, testId }: {
  name: string;
  ruleKey: string;
  showKey?: boolean;
  catalogued?: boolean;
  source: RuleSourceKind;
  sourceLabel: string;
  /** "1 em revisão" — replaces the source label when something waits. */
  pendingLabel?: string;
  selected: boolean;
  density?: 'comfortable' | 'compact';
  onClick: () => void;
  /** A test hook (`data-testid`). */
  testId?: string;
}): ReactElement {
  return (
    <button type="button" data-testid={testId} className="vello-gr-row" aria-current={selected ? 'true' : undefined} data-catalogued={catalogued ? undefined : 'false'} data-density={density === 'compact' ? 'compact' : undefined} onClick={onClick}>
      <span className="vello-gr-dot" data-source={source} aria-hidden="true" />
      <span className="vello-gr-row__main">
        <span className="vello-gr-row__name">{name}</span>
        {showKey && catalogued && <span className="vello-gr-row__key">{ruleKey}</span>}
      </span>
      {pendingLabel !== undefined
        ? <RulePill tone="warning">{pendingLabel}</RulePill>
        : <span className="vello-gr-row__src" data-source={source}>{sourceLabel}</span>}
    </button>
  );
}

export type RulePillTone = 'neutral' | 'warning' | 'success' | 'ink' | 'danger' | 'quiet' | 'outline';

/** The small pills of the screen (a domain, "Vale aqui", "Em vigor", "Legada", "você está aqui"). */
export function RulePill({ tone = 'neutral', children }: { tone?: RulePillTone; children?: ReactNode }): ReactElement {
  return <span className="vello-gr-pill" data-tone={tone === 'neutral' ? undefined : tone}>{children}</span>;
}

/** What the list shows when the search and the filter leave nothing: a title, a hint and the way back. */
export function RuleListEmpty({ title, text, action }: { title: string; text: string; action?: ReactNode }): ReactElement {
  return (
    <div className="vello-gr-listempty" role="status">
      <div className="vello-gr-empty__title">{title}</div>
      <div className="vello-gr-empty__text">{text}</div>
      {action}
    </div>
  );
}

/** The foot disclosure of the list ("Outras políticas (somente leitura)"). */
export function RuleDisclosure({ label, open, onToggle, children }: { label: string; open: boolean; onToggle: () => void; children?: ReactNode }): ReactElement {
  const bodyId = useId();
  return (
    <>
      <button type="button" className="vello-gr-disclosure" aria-expanded={open} aria-controls={bodyId} onClick={onToggle}>
        <span>{label}</span>
        <span className="vello-gr-disclosure__chev" aria-hidden="true"><UiIcon name="chevronDown" size={13} /></span>
      </button>
      {open && <div className="vello-gr-disclosure__body" id={bodyId}>{children}</div>}
    </>
  );
}

// ---- the selected rule ----------------------------------------------------------------------------------------------------------

/** The detail column. */
export function RuleDetail({ label, children }: { label: string; children?: ReactNode }): ReactElement {
  return <section className="vello-gr-detail" aria-label={label}>{children}</section>;
}

/** The title of the selected rule: its domain, its key (with a copy button; left out without `ruleKey`), its name and, under it, its description. */
export function RuleTitle({ domain, ruleKey, name, copyLabel = '', onCopy, description }: { domain: string; ruleKey?: string; name: string; copyLabel?: string; onCopy?: () => void; description?: ReactNode }): ReactElement {
  return (
    <div className="vello-gr-title">
      <div className="vello-gr-title__meta">
        <RulePill tone="ink">{domain}</RulePill>
        {ruleKey !== undefined && (
          <>
            <span className="vello-gr-title__key">{ruleKey}</span>
            {onCopy !== undefined && <button type="button" className="vello-gr-iconbtn" aria-label={copyLabel} title={copyLabel} onClick={onCopy}><UiIcon name="copy" size={12} /></button>}
          </>
        )}
      </div>
      <h2 className="vello-gr-title__name">{name}</h2>
      {description}
    </div>
  );
}

/**
 * D775: what the rule decides (`summary`, always shown) and, behind "Ler mais", how it works with examples (`details`). The long
 * text is plain: paragraphs separated by a blank line, a line starting with "- " is a bullet. It opens in place (no hover, no popup),
 * so it reads with the keyboard and on a phone.
 */
export function RuleDescription({ summary, details, moreLabel, lessLabel }: { summary: string; details?: string; moreLabel: string; lessLabel: string }): ReactElement {
  const [open, setOpen] = useState(false);
  const bodyId = useId();
  const blocks = (details ?? '').split(/\n\s*\n/).map((b) => b.trim()).filter((b) => b !== '');
  return (
    <div className="vello-gr-desc">
      <p className="vello-gr-desc__summary">{summary}</p>
      {open && (
        <div className="vello-gr-desc__more" id={bodyId}>
          {blocks.map((block, i) => {
            const lines = block.split('\n');
            if (lines.every((l) => l.startsWith('- '))) {
              return <ul key={i}>{lines.map((l, j) => <li key={j}>{l.slice(2)}</li>)}</ul>;
            }
            return <p key={i}>{block}</p>;
          })}
        </div>
      )}
      {blocks.length > 0 && (
        <button type="button" className="vello-gr-desc__toggle" aria-expanded={open} aria-controls={bodyId} onClick={() => setOpen((o) => !o)}>
          <span>{open ? lessLabel : moreLabel}</span>
          <span className="vello-gr-disclosure__chev" aria-hidden="true"><UiIcon name="chevronDown" size={13} /></span>
        </button>
      )}
    </div>
  );
}

/** Two columns of cards that fold into one. */
export function RuleColumns({ children }: { children?: ReactNode }): ReactElement {
  return <div className="vello-gr-columns">{children}</div>;
}
export function RuleColumn({ children }: { children?: ReactNode }): ReactElement {
  return <div className="vello-gr-column">{children}</div>;
}

/** A card of the detail: a head (title, an optional subtitle and something on the right), then its body. `flush` leaves the body unpadded. */
export function RuleCard({ title, sub, aside, tone, flush = false, children, label }: {
  title: ReactNode;
  sub?: ReactNode;
  aside?: ReactNode;
  tone?: 'warning';
  flush?: boolean;
  /** The accessible name of the card when the title is not plain text. */
  label?: string;
  children?: ReactNode;
}): ReactElement {
  const titleId = useId();
  return (
    <section className="vello-gr-card" data-tone={tone} {...(label !== undefined ? { 'aria-label': label } : { 'aria-labelledby': titleId })}>
      <div className="vello-gr-card__head">
        {tone === 'warning' && <span className="vello-gr-pending__dot" aria-hidden="true" />}
        <h3 className="vello-gr-card__title" id={titleId}>{title}</h3>
        {sub !== undefined && <span className="vello-gr-card__sub">{sub}</span>}
        {aside !== undefined && <span className="vello-gr-card__aside">{aside}</span>}
      </div>
      {flush ? children : <div className="vello-gr-card__body">{children}</div>}
    </section>
  );
}

/** A padded section of a `flush` card (below a chain or a list of entries). */
export function RuleCardBody({ children }: { children?: ReactNode }): ReactElement {
  return <div className="vello-gr-card__body">{children}</div>;
}

/** A governed value, in mono (`sm` is the compact one of a proposal). */
export function ValueBlock({ size, children }: { size?: 'sm'; children?: ReactNode }): ReactElement {
  return <div className="vello-gr-value" data-size={size}>{children}</div>;
}

/** One line of a structured value: a label and its text, or a group (`rows`) under the label. `was`: the text it had before (a changed field). */
export interface GovernedValueRow {
  readonly label: string;
  readonly text?: string;
  readonly was?: string;
  readonly rows?: readonly GovernedValueRow[];
}

/**
 * A governed value read as a definition list, in the body font: one row per field (label on the left, value on the right), a group
 * indented under its name. A row with `was` is a changed field (the old text struck, the new one marked).
 */
export function ValueList({ rows }: { rows: readonly GovernedValueRow[] }): ReactElement {
  return (
    <dl className="vello-gr-vlist">
      {rows.map((r, i) => {
        const key = `${r.label}|${String(i)}`;
        if (r.rows !== undefined) {
          return r.label === ''
            ? <div className="vello-gr-vlist__group" key={key}><dd className="vello-gr-vlist__val"><ValueList rows={r.rows} /></dd></div>
            : <div className="vello-gr-vlist__group" key={key}><dt className="vello-gr-vlist__key">{r.label}</dt><dd className="vello-gr-vlist__val"><ValueList rows={r.rows} /></dd></div>;
        }
        return (
          <div className="vello-gr-vlist__row" key={key} data-bare={r.label === '' ? 'true' : undefined} data-changed={r.was !== undefined ? 'true' : undefined}>
            {r.label !== '' && <dt className="vello-gr-vlist__key">{r.label}</dt>}
            <dd className="vello-gr-vlist__val">
              {r.was !== undefined && <span className="vello-gr-vlist__was">{r.was}</span>}
              {r.text}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

/** A value kept closed until asked for ("Ver valor completo"): the whole policy is one click away, never a wall on the page. */
export function ValueFold({ label, hideLabel, defaultOpen = false, children }: { label: string; hideLabel?: string; defaultOpen?: boolean; children?: ReactNode }): ReactElement {
  const [open, setOpen] = useState(defaultOpen);
  const bodyId = useId();
  return (
    <div className="vello-gr-vfold">
      <button type="button" className="vello-gr-vfold__toggle" aria-expanded={open} aria-controls={bodyId} onClick={() => setOpen(!open)}>
        <span>{open && hideLabel !== undefined ? hideLabel : label}</span>
        <span className="vello-gr-vfold__chev" aria-hidden="true"><UiIcon name="chevronDown" size={13} /></span>
      </button>
      {open && <div className="vello-gr-vfold__body" id={bodyId}>{children}</div>}
    </div>
  );
}

/** A line of facts separated by dots ("desde …  ·  aprovada por …"). */
export function MetaLine({ children }: { children?: ReactNode }): ReactElement {
  return <div className="vello-gr-meta">{children}</div>;
}

/** Mono, tabular text inside a line (an instant, a version). */
export function MonoText({ children }: { children?: ReactNode }): ReactElement {
  return <span className="vello-gr-mono">{children}</span>;
}

/** "Sem valor em nenhum nível": what a rule with nothing in force shows in place of a value. */
export function EmptyValue({ icon = 'copy', title, text }: { icon?: UiIconName; title: string; text: ReactNode }): ReactElement {
  return (
    <div className="vello-gr-empty">
      <span className="vello-gr-empty__icon" aria-hidden="true"><UiIcon name={icon} size={14} /></span>
      <div>
        <div className="vello-gr-empty__title">{title}</div>
        <div className="vello-gr-empty__text">{text}</div>
      </div>
    </div>
  );
}

/** The "Vai valer em …" note of the proposal card. */
export function LevelNote({ icon, children, aside }: { icon: UiIconName; children?: ReactNode; aside?: ReactNode }): ReactElement {
  return (
    <div className="vello-gr-note">
      <span className="vello-gr-note__icon" aria-hidden="true"><UiIcon name={icon} size={14} /></span>
      <div>{children}{aside !== undefined && <> <span className="vello-gr-note__aside">{aside}</span></>}</div>
    </div>
  );
}

/** The chips of a token list. */
export function OptionChips({ label, children }: { label: string; children?: ReactNode }): ReactElement {
  return <div className="vello-gr-chips" role="group" aria-label={label}>{children}</div>;
}

/** One token of a token list: a pressed toggle (never a primary button), with a check when on. */
export function OptionChip({ on, onClick, children }: { on: boolean; onClick: () => void; children?: ReactNode }): ReactElement {
  return (
    <button type="button" className="vello-gr-chip" aria-pressed={on} onClick={onClick}>
      {on && <UiIcon name="check" size={12} />}
      {children}
    </button>
  );
}

/** The primary action of the proposal card, and the line that says why it is off. */
export function SubmitRow({ action, hint }: { action: ReactNode; hint?: ReactNode }): ReactElement {
  return (
    <div className="vello-gr-submit">
      {action}
      {hint !== undefined && <span className="vello-gr-submit__hint" aria-live="polite">{hint}</span>}
    </div>
  );
}

// ---- the value per level ----------------------------------------------------------------------------------------------------------

/** The chain of levels, most specific first. */
export function LevelChain({ children }: { children?: ReactNode }): ReactElement {
  return <ol className="vello-gr-chain">{children}</ol>;
}

/** One level of the chain: its dot, its name (a button that moves the page to that level), its tag and the value it holds. */
export function LevelChainItem({ label, onOpen, tag, tagTone, here, hereLabel, value, decides, held, below, struck, last }: {
  label: string;
  /** Moves the page to this level; absent when the level is the one on screen. */
  onOpen?: () => void;
  tag: string;
  tagTone: RulePillTone;
  here: boolean;
  hereLabel: string;
  value?: ReactNode;
  decides: boolean;
  held: boolean;
  below: boolean;
  struck: boolean;
  last: boolean;
}): ReactElement {
  return (
    <li className="vello-gr-chain__row" data-decides={decides ? 'true' : undefined} data-held={held && !below ? 'true' : undefined} data-below={below ? 'true' : undefined}>
      <span className="vello-gr-chain__rail" aria-hidden="true">
        <span className="vello-gr-chain__dot" data-state={decides ? 'decides' : held ? 'held' : undefined} />
        {!last && <span className="vello-gr-chain__line" />}
      </span>
      <div className="vello-gr-chain__main">
        <div className="vello-gr-chain__label">
          <button type="button" className="vello-gr-chain__name" onClick={onOpen} disabled={onOpen === undefined} aria-current={here ? 'true' : undefined}>{label}</button>
          <RulePill tone={tagTone}>{tag}</RulePill>
          {here && <RulePill tone="outline">{hereLabel}</RulePill>}
        </div>
        {value !== undefined && <div className="vello-gr-chain__value" data-struck={struck ? 'true' : undefined}>{value}</div>}
      </div>
    </li>
  );
}

// ---- history and pending proposals ------------------------------------------------------------------------------------------------

/** One version of the history: its title and pills, when, the value and where · who. Extra content (a revert) goes below. */
export function HistoryEntry({ title, pills, when, value, by, children }: { title: string; pills?: ReactNode; when: string; value: ReactNode; by: ReactNode; children?: ReactNode }): ReactElement {
  return (
    <div className="vello-gr-item">
      <div className="vello-gr-item__head">
        <span className="vello-gr-item__title">{title}</span>
        {pills}
        <span className="vello-gr-item__when">{when}</span>
      </div>
      <div className="vello-gr-item__value">{value}</div>
      <div className="vello-gr-item__by">{by}</div>
      {children}
    </div>
  );
}

/** One proposal waiting for review: who, when and where, the proposed value, the reason, and the decisions on the right. */
export function PendingProposal({ meta, value, reason, notice, actions }: { meta: ReactNode; value: ReactNode; reason: ReactNode; notice?: ReactNode; actions: ReactNode }): ReactElement {
  return (
    <div className="vello-gr-pending">
      <div className="vello-gr-pending__main">
        <MetaLine>{meta}</MetaLine>
        {typeof value === 'string' ? <ValueBlock size="sm">{value}</ValueBlock> : value}
        <div className="vello-gr-pending__reason">{reason}</div>
        {notice}
      </div>
      <div className="vello-gr-pending__actions">{actions}</div>
    </div>
  );
}
